import { useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { AxiosHttpClient } from 'src/http';
import { API_URL } from 'src/config-global';
import { useUserStore } from 'src/stores/user/user.store';
import { UserRepository } from 'src/repositories/user/user.repository';

import { SplashScreen } from 'src/components/loading-screen';

import { useAuthStore } from '../auth.store';

type Props = {
  children: React.ReactNode;
};

export default function AuthGuard({ children }: Props) {
  const { isLoading } = useAuthStore();

  return <>{isLoading ? <SplashScreen /> : <Container>{children}</Container>}</>;
}

function Container({ children }: Props) {
  const router = useRouter();

  const api = new AxiosHttpClient(API_URL);
  const { user } = useUserStore()
  const repository = new UserRepository(api);

  const { isAuthenticated, accessToken, refreshToken, clearSession, setIsAuthenticated } = useAuthStore();

  const [checked, setChecked] = useState(false);

  const initializeAuth = async () => {
    if (accessToken && refreshToken) {
      const response = await repository.verifyAccessToken(accessToken, user?.id);
      if (response.isLeft()) {
        clearSession();
      }
      setIsAuthenticated(true);
    } else {
      setIsAuthenticated(false);
    }
  };

  const check = useCallback(() => {
    if (!isAuthenticated) {
      const searchParams = new URLSearchParams({
        returnTo: window.location.pathname,
      }).toString();

      const href = `${paths.auth.login}?${searchParams}`;

      router.replace(href);
    } else {
      setChecked(true);
    }
  }, [isAuthenticated, router]);

  const auth = async() => {
    await initializeAuth()
    check()
  }

  useEffect(() => {
    auth()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, router]);

  if (!checked) {
    return null;
  }

  return <>{children}</>;
}
